<template>
  <!-- 对齐 App profile.dart：AppHeader(Profile) + 白色卡片（顶部圆角 30 + 上边距）+ 头像 + 3 输入框 + Save changes -->
  <div class="profile-page">
    <app-header :title="i18n.title"></app-header>

    <div class="profile-card">
      <!-- 头像（点击换头像，对齐 App getImage） -->
      <div class="profile-avatar" @click="onPickAvatar">
        <img :src="avatarShow" alt="" />
      </div>
      <input
        ref="avatarInput"
        class="profile-avatar-input"
        type="file"
        accept="image/*"
        @change="onAvatarChange"
      />

      <!-- First name -->
      <div class="profile-field">
        <input type="text" v-model="firstName" :placeholder="i18n.first_name" />
      </div>

      <!-- Last name -->
      <div class="profile-field">
        <input type="text" v-model="lastName" :placeholder="i18n.last_name" />
      </div>

      <!-- Email address -->
      <div class="profile-field">
        <input type="email" v-model="email" :placeholder="i18n.email_address" />
      </div>

      <!-- Save changes（对齐 App：btnPrimary + margin-top 40） -->
      <div class="btn profile-save" @click="saveInfo">{{ i18n.save_change }}</div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import AppHeader from "../../components/common/AppHeader.vue"
import { USER_INFO, UPLOAD_IMAGE, SAVE_USER_AVATAR } from "../../apis/user"

export default {
  components: {
    AppHeader
  },
  computed: {
    ...mapState(["user"]),
    i18n() {
      return this.$t("profile")
    },
    // 无头像时兜底占位图（与 mine 页 avatarUrl 处理一致）
    avatarShow() {
      return this.avatarUrl || require("../../assets/imgs/avatar.png")
    }
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      avatarUrl: ""
    }
  },
  created() {
    // 对齐 App initState：从用户信息初始化（Global.profile.user）
    const u = this.user || {}
    this.firstName = u.firstName || ""
    this.lastName = u.lastName || ""
    this.email = u.email || ""
    this.avatarUrl = u.avatarUrl || ""
  },
  methods: {
    onPickAvatar() {
      this.$refs.avatarInput && this.$refs.avatarInput.click()
    },
    // 对齐 App getImage：相册选图 → oss/upload → saveUserAvatar
    onAvatarChange(e) {
      const file = e.target.files && e.target.files[0]
      // 重置 value，保证连续选择同一文件也能再次触发 change
      e.target.value = ""
      if (!file) return
      this.$loading(true)
      const formData = new FormData()
      formData.append("file", file, file.name || "temp.png")
      this.$post(
        UPLOAD_IMAGE,
        formData,
        (resp) => {
          // App: result["original"]（响应体顶层字段）
          const path = resp && resp.original
          if (!path) {
            this.$loading(false)
            return this.$toast(this.$t("mine.msg_fail_update"))
          }
          this.$post(
            SAVE_USER_AVATAR,
            { path },
            () => {
              this.$loading(false)
              this.avatarUrl = path
              // 同步 store，返回 mine 页头像即时可见
              this.$store.commit("user", { ...(this.user || {}), avatarUrl: path })
            },
            (err) => {
              this.$loading(false)
              this.$toast(err)
            }
          )
        },
        (err) => {
          this.$loading(false)
          this.$toast(err)
        },
        // http.js 仅在 Content-Type 非 form-urlencoded 时跳过 qs.stringify，
        // axios 0.19 对 FormData 会删除该头交由浏览器自动补 boundary
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    },
    // 对齐 App func.dart isEmail
    isEmail(input) {
      return /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(input)
    },
    // 对齐 App saveInfo：校验 → POST userInfo → GET userInfo 刷新 → toast → 返回
    saveInfo() {
      if (this.firstName === "") {
        return this.$toast(this.i18n.enter_your_nickname)
      }
      if (this.email === "") {
        return this.$toast(this.i18n.enter_your_email)
      }
      if (!this.isEmail(this.email)) {
        return this.$toast(this.i18n.email_format_incorrect)
      }
      this.$loading(true)
      this.$post(
        USER_INFO,
        {
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName
        },
        () => {
          // 对齐 App：保存成功后 getUserInfo 刷新用户
          this.$get(
            USER_INFO,
            {},
            (resp) => {
              this.$loading(false)
              if (resp.user) {
                this.$store.commit("user", resp.user)
              }
              this.$toast(this.i18n.change_success)
              this.$router.go(-1)
            },
            (err) => {
              this.$loading(false)
              this.$toast(err)
            }
          )
        },
        (err) => {
          this.$loading(false)
          this.$toast(err)
        },
        // 对齐 App saveUserInfo 的 post(..., true)：该接口 @RequestBody 仅收 JSON，
        // http.js 检测到非 form-urlencoded 自动跳过 qs.stringify，axios 按 JSON 序列化
        { headers: { "Content-Type": "application/json;charset=UTF-8" } }
      )
    }
  }
}
</script>

<style lang="less">
/* 对齐 App profile.dart：白底 page + 白色卡片（顶部圆角 30 + 10.w 上边距） */
.profile-page {
  position: relative;
  width: 100%;
  min-height: 100vh;
  background: var(--page-bg);
  display: flex;
  flex-direction: column;
}

.profile-card {
  flex: 1;
  background: var(--card-bg);
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  margin-top: 17px;
  padding: 17px 35px 80px;
  box-sizing: border-box;
}

// 头像（对齐 App 80 圆形 ClipOval，H5 与 mine 页头像同尺寸 160px）
.profile-avatar {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  overflow: hidden;
  background: #d8d6dc;
  margin: 35px auto 0;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

// 隐藏的文件选择框（对齐 App ImagePicker 相册选图）
.profile-avatar-input {
  display: none;
}

// 输入框（对齐 App：透明背景 + 圆角 30 边框，聚焦主题色 / 失焦 #D5D5D5）
.profile-field {
  margin-top: 28px;
  height: 105px;
  display: flex;
  align-items: center;
  padding: 0 35px;
  box-sizing: border-box;
  border: 1px solid #d5d5d5;
  border-radius: 28px;

  // 对齐 App：聚焦态边框变主题色（focusNode listener 的 CSS 实现）
  &:focus-within {
    border-color: var(--theme-color);
  }

  input {
    flex: 1;
    height: 80px;
    font-size: 35px;
    border: none;
    outline: none;
    background-color: rgba(0, 0, 0, 0);
    color: var(--text-main);

    // 对齐 App：hint 灰色 16sp
    &::placeholder {
      color: var(--text-hint);
    }
  }
}

// Save changes（对齐 App margin-top 40 → 70px）
.profile-save {
  margin-top: 70px;
}
</style>
